<template>
    <ElementLayout>
        <template #element>
            <div class="eventselect-info" v-if="areas && areas.length == 0">{{ form$.translations.festiware.events.no_events }}</div>
            <div class="events-selection-hint uk-margin-small-bottom" v-if="selectionHint">{{ selectionHint }}</div>
            <div class="">
                <div v-for="areaEvents in areas">
                    <div class="scheduler-area-heading">{{ areaEvents[0].area }}</div>
                    <div class="uk-overflow-auto uk-margin-bottom">
                        <table :class="{'uk-table uk-table-small uk-table-justify uk-table-striped events-table' : true, 'disabled' : false}">
                            <thead>
                            <tr>
                                <th style="width: 35px" v-show="!settings.readonly"></th>
                                <th style="width: 25%">
                                    {{ settings.fullDaySelect ? form$.translations.festiware.events.date : form$.translations.festiware.events.datetime }}
                                </th>
                                <th style="width: 15%" v-if="!settings.fullDaySelect">
                                    {{ form$.translations.festiware.events.duration }}
                                </th>
                                <th v-if="!settings.hideEventName || typeof areaEvents[0].jobs != 'undefined'">Details</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(event, index) in areaEvents" :data-id="event.id"
                                :class="{'event-row': true, 'new-area': index == 0, 'assigned': event.assigned, 'selected-scheduler-event': selectedEvents.includes(event.id)}"
                                @click="toggleActive(event, $event)"
                                :uk-tooltip="event.assigned ? form$.translations.festiware.events.not_available : null"
                            >
                                <td class="uk-table-middle checkbox" v-show="!settings.readonly">
                                    <input v-if="!event.assigned" class="uk-checkbox" type="checkbox" :checked="selectedEvents.includes(event.id)">
                                </td>
                                <td class="uk-table-middle uk-text-nowrap">
                                    {{ event.date }}
                                </td>
                                <td class="uk-table-middle" v-if="!settings.fullDaySelect">
                                    {{ event.duration }}
                                </td>
                                <td class="uk-table-middle" v-if="!settings.hideEventName || event.jobs" >
                                    <div v-html="event.details" class="event-details"></div>
                                    <div v-if="event.jobs && event.jobs.length == 1">
                                        {{ event.jobs[0].name }}
                                    </div>
                                    <div class="joblist" v-else-if="event.jobs && event.jobs.length > 1">
                                        <template v-for="(group, index) in jobGroups(event.jobs)" :key="index">
                                            <label :class="{ assigned: group.freeCount === 0 }" :uk-tooltip="group.freeCount === 0 && selectedEvents.includes(event.id) ? form$.translations.festiware.events.not_available : null"
                                               v-if="!settings.readonly || group.jobs.some(job => selectedJobs.includes(job.id))"
                                            >
                                                <input
                                                    autocomplete="off"
                                                    class="uk-radio uk-margin-small-right"
                                                    type="radio"
                                                    :value="group.jobs[0].id"
                                                    :name="'job-select-'+event.id"
                                                    @change="toggleJob(event, group, $event)"
                                                    :disabled="!selectedEvents.includes(event.id) || group.freeCount === 0"
                                                    :checked="group.jobs.some(job => selectedJobs.includes(job.id))" />
                                                {{ group.name }}<span v-if="group.jobs.length > 1"> ({{ form$.translations.festiware.events.free_slots.replace(':count', group.freeCount) }})</span>
                                                <br/>
                                            </label>
                                        </template>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <!-- Default element slots -->
        <template v-for="(component, slot) in elementSlots" #[slot]><slot :name="slot" :el$="el$"><component :is="component" :el$="el$"/></slot></template>
    </ElementLayout>
</template>

<script>
import { defineElement } from '@vueform/vueform'
import {computed, onMounted, nextTick, ref} from "vue";

function groupJobsByName(jobs) {
    const groups = [];
    const indexByName = new Map();
    for (const job of jobs) {
        if (!indexByName.has(job.name)) {
            indexByName.set(job.name, groups.length);
            groups.push({ name: job.name, jobs: [] });
        }
        groups[indexByName.get(job.name)].jobs.push(job);
    }
    return groups.map(group => ({
        ...group,
        freeCount: group.jobs.filter(job => !job.assigned).length,
    }));
}

export default defineElement({
    props: {settings: {required: true, type: Object}},
    name: 'EventsElement',
    setup(props, ctx) {
        const context = {...ctx};
        const element = context.element;
        const { update } = element
        let selectedEvents = ref([]);
        let selectedJobs = ref([]);
        onMounted(() => {
            element.update(props.settings.selected.value)
            if (props.settings.selected.value) {
                selectedEvents.value = props.settings.selected.value.events??[];
                selectedJobs.value = props.settings.selected.value.jobs??[];
            }
        })
        return {
            defaultValue: {jobs: [], events: []},
            areas: computed(() => {
                return Object.values(props.settings.events).filter(areaEvents => {
                    return Object.values(areaEvents).filter(event => !event.assigned).length;
                })
            }),
            selectedJobs,
            selectedEvents,
            jobGroups: groupJobsByName,
            selectionHint: computed(() => {
                const min = props.settings.minimumSelection || 0;
                const max = props.settings.maximumSelection || 0;
                if (!min && !max) {
                    return '';
                }
                const t = element.form$.value.translations.festiware.events;
                const selected = selectedEvents.value.length;
                let template;
                if (min > 0 && max > 0 && min === max) {
                    template = t.selection_hint_exact.replace(':min', min);
                } else if (min > 0 && max > 0) {
                    template = t.selection_hint_range.replace(':min', min).replace(':max', max);
                } else if (min > 0) {
                    template = t.selection_hint_min.replace(':min', min);
                } else {
                    template = t.selection_hint_max.replace(':max', max);
                }
                return template.replace(':selected', selected);
            }),
            toggleActive(event, e) {
                element.form$.value.isDirty = true;
                const rowEl = e.currentTarget;
                try {
                    let selectedEventsArray = element.model && element.model.value && element.model.value.events ? element.model.value.events : [];
                    let eventCurrentlySelected = selectedEventsArray.includes(event.id);
                    if (event.assigned || this.readonly || (eventCurrentlySelected && e.target.className != 'uk-checkbox' && (e.target.nodeName == 'INPUT' || e.target.nodeName == 'LABEL'))) {
                        return
                    }
                    if (eventCurrentlySelected) {
                        selectedEvents.value.splice(selectedEvents.value.indexOf(event.id), 1);
                        // remove job from value
                        if (typeof event.jobs != 'undefined') {
                            for (let jidx in event.jobs) {
                                let index = selectedJobs.value.indexOf(event.jobs[jidx].id);
                                if (index > -1) {
                                    selectedJobs.value.splice(index, 1);
                                }
                            }
                        }
                    } else {
                        if (this.settings.onlyOneArea && selectedEvents.value.length > 0) {
                            for (let idx in this.settings.entries) {
                                for (let idx2 in this.settings.entries[idx]) {
                                    let e = this.settings.entries[idx][idx2]
                                    if (e.id == this.selectedEvents[0] && e.area != event.area) {
                                        UIkit.notification(this.form$.translations.festiware.events.only_one_place);
                                        return;
                                    }
                                }
                            }
                        }
                        if (this.settings.maximumSelection != 0 && this.selectedEvents.length >= this.settings.maximumSelection) {
                            UIkit.notification(this.form$.translations.festiware.events.maximum_reached);
                            return;
                        }
                        selectedEvents.value.push(event.id);
                        if (event.jobs && event.jobs.length) {
                            const notAssigned = event.jobs.find(job => !job.assigned)
                            if (notAssigned) {
                                selectedJobs.value.push(notAssigned.id);
                            }
                        }
                    }

                    if (!element.model.value) {
                      element.model.value = {};
                    }

                    element.model.value.events = this.selectedEvents;
                    element.model.value.jobs = this.selectedJobs;
                } finally {
                    // Belt-and-suspenders: a click landing directly on the checkbox lets the
                    // browser toggle its native checked state before this handler runs, which can
                    // leave the DOM property out of sync with Vue's :checked binding (observed in
                    // Firefox) — regardless of whether the selection above went through or was
                    // blocked. Force it back in line with the real selection on every exit path.
                    nextTick(() => {
                        const checkbox = rowEl.querySelector('input.uk-checkbox');
                        if (checkbox) {
                            checkbox.checked = selectedEvents.value.includes(event.id);
                        }
                    });
                }
            },
            toggleJob(event, group, e) {
                element.form$.value.isDirty = true;
                const eventJobIds = event.jobs.map(job => job.id);
                this.selectedJobs = this.selectedJobs.filter(jobId => !eventJobIds.includes(jobId));
                const target = group.jobs.find(job => !job.assigned) ?? group.jobs[0];
                this.selectedJobs.push(target.id);
                element.model.value.jobs = this.selectedJobs;
            },
        }
    }
})
</script>
